import { useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { rankWith, type ControlProps, type RankedTester } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { CATALOG_RENDERER_NAMES } from "../../shared/constants/catalogReviewConstants";
import { CaseReviewIcon } from "../../shared/components/field_review/CaseReviewIcon";
import { CaseFieldRow } from "../../shared/renderers/CaseReviewRenderers";
import { useCaseFieldReview } from "../../shared/hooks/useCaseFieldReview";
import { useMedDraSearch } from "../hooks/useMedDraSearch";
import { MEDDRA_MIN_QUERY } from "../services/meddraLookupService";

/**
 * The preferred term of an indication, coded against MedDRA.
 *
 * <p>The only control in the catalog that asks a service what a reviewer may choose, so it is the
 * only one whose empty list has two meanings. It says which one it means.
 *
 * <p>Choosing a term fills the lower-level term beside it, as the legacy screen did: the two are
 * one act of coding, and leaving the second for the reviewer to copy is how they end up
 * disagreeing.
 */
const LOWER_LEVEL_TERM = "llt";

function MedDraLookupControl(props: ControlProps) {
  const field = useCaseFieldReview(props);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const search = useMedDraSearch(query, open);

  const choose = (preferredTerm: string, lowerLevelTerm: string) => {
    field.update(preferredTerm);
    field.context.edit(LOWER_LEVEL_TERM);
    field.context.changeField(LOWER_LEVEL_TERM, lowerLevelTerm);
    setOpen(false);
  };

  const value = typeof props.data === "string" ? props.data : "";
  return <CaseFieldRow props={props} field={field}>{(editable) =>
    <PopoverPrimitive.Root open={open} onOpenChange={(next) => { setOpen(next); if (!next) setQuery(""); }}>
    <PopoverPrimitive.Trigger id={field.inputId} className="cr-select-trigger" role="combobox"
      aria-expanded={open} aria-label={field.label} disabled={!editable} data-empty={!value}>
      <span className="cr-select-value">{value || "— search MedDRA —"}</span>
      <span className="cr-select-chevron"><CaseReviewIcon name="search" size={12} strokeWidth={2.5} /></span>
    </PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content className="cr-portal cr-select-content cr-combobox" sideOffset={4} align="start">
        <Command className="cr-command" loop shouldFilter={false}>
          <div className="cr-command-search">
            <CaseReviewIcon name="search" size={14} />
            <Command.Input value={query} onValueChange={setQuery} autoFocus className="cr-command-input"
              placeholder={`Search MedDRA (${MEDDRA_MIN_QUERY}+ characters)…`} />
          </div>
          <Command.List className="cr-select-viewport">
            {search.status === "idle" && <p className="cr-select-empty" role="status">Type at least {MEDDRA_MIN_QUERY} characters.</p>}
            {search.status === "searching" && <p className="cr-select-empty" role="status">Searching MedDRA…</p>}
            {search.status === "empty" && <p className="cr-select-empty" role="status">No MedDRA term matches this text.</p>}
            {search.status === "failed" && <p className="cr-select-error" role="alert">MedDRA lookup failed. The dictionary could not be searched — this is not the same as no match.</p>}
            {search.matches.map((match) => <Command.Item key={`${match.preferredTerm}|${match.lowerLevelTerm}`}
              value={match.preferredTerm} className="cr-select-item"
              onSelect={() => choose(match.preferredTerm, match.lowerLevelTerm)}>
              <span className="cr-select-value">{match.preferredTerm}</span>
              {match.lowerLevelTerm !== match.preferredTerm
                && <span className="cr-select-hint">{match.lowerLevelTerm}</span>}
            </Command.Item>)}
          </Command.List>
        </Command>
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>}
  </CaseFieldRow>;
}

/** Ranked above the ordinary control, so only the field the catalog marks for it is affected. */
export const medDraLookupTester: RankedTester = rankWith(11, (uischema) =>
  (uischema as { options?: { renderer?: string } }).options?.renderer === "meddra_lookup");

export const medDraLookupRenderer = withJsonFormsControlProps(MedDraLookupControl);
